import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Download, Plus, Printer, Shield } from "lucide-react";
import { toast } from "sonner";
import api, { API, humanizeServiceError } from "../../lib/api";
import {
  BackLink,
  HeroStatus,
  Section,
  Card,
  CardTitle,
  ProgressCard,
  KvGrid,
  KvItem,
  FooterActions,
  ActionButton,
  TechDetailsToggle,
  PdfCard,
  fmtItalianDateTime,
  timeAgo,
  useNow,
  upperOrDash,
  EM_DASH,
} from "./Primitives";
import { PROVINCE_BY_SIGLA } from "../../lib/provinces";

const SERVICE_LABEL = {
  visura_catastale_immobile_pdf: "Immobile (PDF ufficiale AdE)",
  visura_catastale_soggetto_pg_pdf: "Soggetto - Persona giuridica (PDF ufficiale AdE)",
  visura_catastale_soggetto_pf_pdf: "Soggetto - Persona fisica (PDF ufficiale AdE)",
  planimetria_catastale_pdf: "Elaborato planimetrico (schema subalterni particella)",
};

const upperFirst = (s) => (s && typeof s === "string" ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const buildRequestKv = (request) => {
  const p = request?.params || {};
  const provLabel = p.provincia ? `${p.provincia} · ${PROVINCE_BY_SIGLA[p.provincia]?.nome || ""}`.trim() : EM_DASH;
  const tipoCat = p.tipo_catasto === "F" ? "Fabbricati" : p.tipo_catasto === "T" ? "Terreni" : EM_DASH;
  const tipoVis = p.tipo_visura ? upperFirst(p.tipo_visura) : EM_DASH;
  const sid = request?.service_id;

  if (sid === "planimetria_catastale_pdf") {
    const items = [
      <KvItem key="comune" label="Comune" value={upperOrDash(p.comune)} />,
      <KvItem key="prov" label="Provincia" value={provLabel} />,
      <KvItem key="fpr" label="Foglio · Particella" value={`${p.foglio || EM_DASH} · ${p.particella || EM_DASH}`} mono />,
    ];
    if (p.sezione) items.push(<KvItem key="sez" label="Sezione" value={upperOrDash(p.sezione)} />);
    if (p.sezione_urbana) items.push(<KvItem key="sezu" label="Sezione urbana" value={upperOrDash(p.sezione_urbana)} />);
    return items;
  }

  const items = [
    <KvItem key="prov" label="Provincia" value={provLabel} />,
    <KvItem key="cat" label="Tipo catasto" value={tipoCat} />,
    <KvItem key="vis" label="Tipo visura" value={tipoVis} />,
  ];

  if (sid === "visura_catastale_immobile_pdf") {
    items.unshift(<KvItem key="comune" label="Comune" value={upperOrDash(p.comune)} />);
    if (p.indirizzo) {
      items.push(<KvItem key="ind" label="Indirizzo" value={`${upperOrDash(p.indirizzo)}, ${p.civico || ""}`} />);
    } else if (p.foglio) {
      items.push(<KvItem key="fpr" label="Foglio · Particella" value={`${p.foglio} · ${p.particella}${p.subalterno ? ` sub ${p.subalterno}` : ""}`} mono />);
    }
  }
  if (sid === "visura_catastale_soggetto_pg_pdf") {
    items.unshift(<KvItem key="cfp" label="CF / P.IVA" value={upperOrDash(p.cf_piva)} mono />);
  }
  if (sid === "visura_catastale_soggetto_pf_pdf") {
    items.unshift(<KvItem key="cfpf" label="Codice Fiscale" value={upperOrDash(p.cf)} mono />);
  }
  return items;
};

export const ResultVisuraCatastaleUfficiale = ({ request: initial, onUpdate, onNewSearch }) => {
  const navigate = useNavigate();
  const [request, setRequest] = useState(initial);
  useEffect(() => setRequest(initial), [initial]);
  useNow(10000);

  // Polling auto ogni 10s
  useEffect(() => {
    const pending = request?.status === "pending_external" || request?.status === "in_progress";
    if (!pending || !request?.id || String(request.id).startsWith("preview-")) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/requests/${request.id}`);
        setRequest(data);
        onUpdate?.(data);
      } catch { /* noop */ }
    }, 10000);
    return () => clearInterval(t);
  }, [request?.id, request?.status, onUpdate]);

  const onManualRefresh = async () => {
    if (!request?.id || String(request.id).startsWith("preview-")) return;
    try {
      const { data } = await api.get(`/requests/${request.id}`);
      setRequest(data);
      onUpdate?.(data);
    } catch { /* noop */ }
  };

  const onPrint = () => window.print();
  const handleNewSearch = onNewSearch || (() => navigate("/search/catasto-documenti"));

  const onDownloadPdf = async () => {
    if (!request?.id) return;
    try {
      const token = localStorage.getItem("wattline_token");
      const res = await fetch(`${API}/requests/${request.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let detail = "Errore download PDF";
        try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch { /* */ }
        toast.error(detail);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = request.pdf_filename || `visura_catastale_${request.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Errore download PDF: ${e?.message || e}`);
    }
  };

  const onExportJson = () => {
    const payload = { id: request.id, service_id: request.service_id, params: request.params, response_data: request.response_data };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `visura_${request.id.slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const tipologia = SERVICE_LABEL[request?.service_id] || request?.service_name || EM_DASH;
  const requestKv = buildRequestKv(request);
  const isPlanimetria = request?.service_id === "planimetria_catastale_pdf";
  const docLabel = isPlanimetria ? "Elaborato planimetrico" : "Visura catastale";

  /* ----------------------- IN-PROGRESS ----------------------- */
  if (request?.status === "pending_external" || request?.status === "in_progress" || request?.status === "pending_confirmation") {
    const since = timeAgo(request?.created_at);
    return (
      <div className="max-w-3xl" data-testid="result-visura-uff-in-progress">
        <BackLink to="/history" label="Storico richieste" />
        <HeroStatus
          variant="info"
          title={`${docLabel} in lavorazione`}
          description="L'Agenzia delle Entrate sta elaborando la richiesta · controlliamo ogni 30 secondi"
          testId="result-hero"
        />
        <Section>
          <ProgressCard
            title="Attesa risposta da Agenzia delle Entrate"
            description={`Richiesta inviata ${since}. Tempo medio di elaborazione: 1–5 minuti.`}
          />
        </Section>
        <Section>
          <Card>
            <CardTitle>Dati richiesta</CardTitle>
            <KvGrid columns={2}>
              <KvItem label="Tipologia" value={tipologia} />
              <KvItem
                label="Stato"
                value={<span className="text-warning-strong font-medium">{request?.openapi_state ? upperFirst(request.openapi_state) : "In erogazione"}</span>}
              />
              {requestKv}
              <KvItem label="Richiesta inviata" value={fmtItalianDateTime(request?.created_at)} />
            </KvGrid>
          </Card>
        </Section>
        <FooterActions>
          <ActionButton variant="outline" icon={RefreshCw} onClick={onManualRefresh} testId="action-refresh">
            Aggiorna stato
          </ActionButton>
          <ActionButton variant="outline" icon={Plus} onClick={handleNewSearch} testId="action-new-search">
            Nuova ricerca
          </ActionButton>
        </FooterActions>
        <TechDetailsToggle data={request} />
      </div>
    );
  }

  /* ----------------------- COMPLETED ----------------------- */
  if (request?.status === "completed") {
  return (
    <div className="max-w-4xl" data-testid="result-visura-uff-completed">
      <BackLink to="/history" label="Storico richieste" />
      <HeroStatus
        variant="success"
        title={`${docLabel} disponibile`}
        description={isPlanimetria
          ? "Elaborato planimetrico rasterizzato dagli archivi dell'Agenzia delle Entrate"
          : "Documento ufficiale firmato digitalmente dall'Agenzia delle Entrate"}
        testId="result-hero"
      />

      <Section>
        <PdfCard
          filename={request.pdf_filename || `visura_catastale_${request.id?.slice(0, 8)}.pdf`}
          sizeLabel={request.pdf_size_bytes ? `${(request.pdf_size_bytes / 1024).toFixed(1)} KB` : null}
          generatedAt={fmtItalianDateTime(request.completed_at || request.created_at)}
          onDownload={onDownloadPdf}
          onPreview={async () => {
            try {
              const token = localStorage.getItem("wattline_token");
              const res = await fetch(`${API}/requests/${request.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
              if (!res.ok) {
                let detail = "Errore apertura PDF";
                try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch { /* */ }
                toast.error(detail);
                return;
              }
              const b = await res.blob();
              window.open(URL.createObjectURL(b), "_blank");
            } catch (e) {
              toast.error(`Errore apertura PDF: ${e?.message || e}`);
            }
          }}
        />
      </Section>

      <Section>
        <Card>
          <CardTitle>
            <span className="inline-flex items-center gap-1.5">
              <Shield size={14} className="text-success-strong" /> Documento ufficiale
            </span>
          </CardTitle>
          <div className="text-sm text-text-secondary leading-relaxed mb-3">
            {isPlanimetria ? (
              <>Il PDF è l'<strong>elaborato planimetrico</strong> estratto dagli archivi
              dell'Agenzia delle Entrate: schema ufficiale dei subalterni della particella
              (piani e unità), utile per due diligence immobiliare e dossier tecnici.
              Non è la planimetria interna delle singole unità immobiliari.</>
            ) : (
              <>Il PDF è stato emesso direttamente dall'<strong>Agenzia delle Entrate</strong> ed è firmato
              digitalmente. È un documento probatorio valido per dossier contrattuali, KYC e adempimenti
              normativi. La firma può essere verificata aprendolo con Acrobat Reader o tool come Dike.</>
            )}
          </div>
        </Card>
      </Section>

      <Section>
        <Card>
          <CardTitle>Dati richiesta</CardTitle>
          <KvGrid columns={2}>
            <KvItem label="Tipologia" value={tipologia} />
            {requestKv}
            <KvItem label="Richiesta evasa" value={fmtItalianDateTime(request.completed_at || request.created_at)} />
            <KvItem label="ID OpenAPI" value={upperOrDash(request.openapi_request_id)} mono />
          </KvGrid>
        </Card>
      </Section>

      <FooterActions>
        <ActionButton variant="primary" icon={Download} onClick={onDownloadPdf} testId="action-download-pdf">
          Scarica PDF
        </ActionButton>
        <ActionButton variant="outline" icon={Plus} onClick={handleNewSearch} testId="action-new-search">
          Nuova ricerca
        </ActionButton>
        <ActionButton variant="outline" icon={Download} onClick={onExportJson} testId="action-export-json">
          Esporta JSON
        </ActionButton>
        <ActionButton variant="outline" icon={Printer} onClick={onPrint} testId="action-print">
          Stampa
        </ActionButton>
      </FooterActions>
      <TechDetailsToggle data={request} />
    </div>
  );
  }

  /* ----------------------- FAILED ----------------------- */
  const friendlyError = humanizeServiceError(request?.error_message, request?.service_id)
    || request?.error_message || "Errore non specificato";
  return (
    <div className="max-w-4xl" data-testid="result-visura-uff-failed">
      <BackLink to="/history" label="Storico richieste" />
      <HeroStatus
        variant="danger"
        title={`${docLabel} non disponibile`}
        description="La richiesta non è andata a buon fine — nessun costo addebitato"
        testId="result-hero"
      />
      <Section>
        <Card>
          <CardTitle>Motivo</CardTitle>
          <div className="text-sm leading-relaxed" style={{color: "var(--wl-danger-text, #B91C1C)"}} data-testid="failed-reason">
            {friendlyError}
          </div>
        </Card>
      </Section>
      <Section>
        <Card>
          <CardTitle>Dati richiesta</CardTitle>
          <KvGrid columns={2}>
            <KvItem label="Tipologia" value={tipologia} />
            {requestKv}
            <KvItem label="Richiesta inviata" value={fmtItalianDateTime(request?.created_at)} />
            <KvItem label="Stato esterno" value={request?.openapi_state || EM_DASH} />
          </KvGrid>
        </Card>
      </Section>
      <FooterActions>
        <ActionButton variant="primary" icon={Plus} onClick={handleNewSearch} testId="action-new-search">
          Riprova con nuova ricerca
        </ActionButton>
      </FooterActions>
      <TechDetailsToggle data={request} />
    </div>
  );
};
